import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AppWindow,
  Blocks,
  KeyRound,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { SOUSA_MURRAY_IDENTITY, identityHost } from '../lib/identity-provider';
import { api } from '../lib/api';
import { Breadcrumbs, Card, EmptyState, Status, Table } from '../components/UI';

const req=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const label=value=>String(value??'').replaceAll('_',' ').replace(/\b\w/g,c=>c.toUpperCase());

export default function IdentityAdministration(){
  const[tab,setTab]=useState('overview');
  const[overview,setOverview]=useState(null);
  const[users,setUsers]=useState([]);
  const[applications,setApplications]=useState([]);
  const[groups,setGroups]=useState([]);
  const[roles,setRoles]=useState([]);
  const[logs,setLogs]=useState([]);
  const[query,setQuery]=useState('');
  const[loading,setLoading]=useState(true);
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');
  const[newUser,setNewUser]=useState({displayName:'',email:''});
  const[newApp,setNewApp]=useState({clientId:'',name:'',clientType:'first_party',redirectUri:'https://my.sousamurray.co.uk/auth/callback'});
  const[newGroup,setNewGroup]=useState({displayName:'',description:'',groupType:'security'});

  const loadOverview=async()=>{
    setLoading(true);setNotice('');
    try{setOverview(await api('/api/identity'));}
    catch(error){setNotice(`Error: ${error.message}`);}
    finally{setLoading(false);}
  };
  useEffect(()=>{loadOverview();},[]);

  const run=async(action,success,after)=>{
    setBusy(true);setNotice('');
    try{
      const result=await action();
      setNotice(success||'Saved.');
      if(after)await after(result);
      return result;
    }catch(error){
      setNotice(`Error: ${error.message}`);
      return null;
    }finally{setBusy(false);}
  };

  const searchUsers=async event=>{
    event?.preventDefault();
    setBusy(true);setNotice('');
    try{
      const result=await api(`/api/identity/admin/identities${query.trim()?`?search=${encodeURIComponent(query.trim())}`:''}`);
      setUsers(result.identities||[]);
    }catch(error){setNotice(`Error: ${error.message}`);}
    finally{setBusy(false);}
  };

  const createUser=event=>{
    event.preventDefault();
    run(
      ()=>api('/api/identity/admin/identities',req('POST',{...newUser,status:'active',sendInvitation:true})),
      'Sousa Murray ID created and setup email requested.',
      async()=>{setNewUser({displayName:'',email:''});await searchUsers();}
    );
  };

  const updateUserStatus=(row,status)=>run(
    ()=>api(`/api/identity/admin/identities/${encodeURIComponent(row.smid)}/status`,req('PATCH',{status})),
    status==='active'?'Sousa Murray ID restored.':'Sousa Murray ID suspended.',
    searchUsers
  );

  const sendInvitation=row=>run(
    ()=>api(`/api/identity/admin/identities/${encodeURIComponent(row.smid)}/send-invitation`,req('POST',{})),
    'Setup email sent.'
  );

  const loadApplications=async()=>{
    const result=await api('/api/identity/admin/applications');
    setApplications(result.applications||[]);
  };
  const createApplication=event=>{
    event.preventDefault();
    run(
      ()=>api('/api/identity/admin/applications',req('POST',{
        clientId:newApp.clientId.trim(),
        name:newApp.name.trim(),
        clientType:newApp.clientType,
        redirectUris:[newApp.redirectUri.trim()].filter(Boolean),
      })),
      'Application created in Sousa Murray ID.',
      async()=>{setNewApp({clientId:'',name:'',clientType:'first_party',redirectUri:'https://my.sousamurray.co.uk/auth/callback'});await loadApplications();}
    );
  };

  const loadDirectory=async()=>{
    const[g,r]=await Promise.all([
      api('/api/identity/admin/directory/groups'),
      api('/api/identity/admin/directory/roles'),
    ]);
    setGroups(g.groups||[]);
    setRoles(r.roles||[]);
  };
  const createGroup=event=>{
    event.preventDefault();
    run(
      ()=>api('/api/identity/admin/directory/groups',req('POST',newGroup)),
      'Directory group created.',
      async()=>{setNewGroup({displayName:'',description:'',groupType:'security'});await loadDirectory();}
    );
  };

  const loadSecurity=async()=>{
    const result=await api('/api/identity/admin/logs');
    setLogs(result.events||[]);
  };

  useEffect(()=>{
    if(tab==='users'&&!users.length)searchUsers();
    if(tab==='applications'&&!applications.length)loadApplications().catch(error=>setNotice(`Error: ${error.message}`));
    if(tab==='directory'&&!groups.length&&!roles.length)loadDirectory().catch(error=>setNotice(`Error: ${error.message}`));
    if(tab==='security'&&!logs.length)loadSecurity().catch(error=>setNotice(`Error: ${error.message}`));
  },[tab]);

  const tabs=[
    ['overview','Overview',ShieldCheck],
    ['users','Users',UserRound],
    ['applications','Applications',AppWindow],
    ['directory','Directory',UsersRound],
    ['security','Security & logs',Activity],
  ];

  return <>
    <Breadcrumbs items={['Administration','Sousa Murray ID']}/>
    <section className="identity-hero">
      <div>
        <span className="record-kicker">Sousa Murray ID</span>
        <h1 className="govuk-heading-xl">Identity administration</h1>
        <p className="govuk-body">Manage the live Sousa Murray identity provider directly from Customer Operations.</p>
      </div>
      <div className="identity-hero-status">
        <KeyRound size={22}/>
        <div>
          <strong>{overview?.connected?'Sousa Murray ID connected':'Checking identity service'}</strong>
          <span>{identityHost(SOUSA_MURRAY_IDENTITY.issuer)}</span>
        </div>
      </div>
    </section>

    <div className="identity-tabs" role="group" aria-label="Identity administration views">
      {tabs.map(([id,text,Icon])=><button type="button" key={id} aria-pressed={tab===id} className={tab===id?'active':''} onClick={()=>setTab(id)}><Icon size={16}/>{text}</button>)}
    </div>

    {notice&&<div className={`operation-notice ${notice.startsWith('Error:')?'error':''}`}>{notice}</div>}

    {loading?<EmptyState title="Loading Sousa Murray ID" body="Reading the live identity service…"/>:
      tab==='overview'?<Overview data={overview} busy={busy} refresh={loadOverview}/>:
      tab==='users'?<Users users={users} query={query} setQuery={setQuery} search={searchUsers} busy={busy} form={newUser} setForm={setNewUser} create={createUser} changeStatus={updateUserStatus} invite={sendInvitation}/>:
      tab==='applications'?<Applications applications={applications} busy={busy} form={newApp} setForm={setNewApp} create={createApplication} refresh={()=>run(loadApplications,'Applications refreshed.',null)}/>:
      tab==='directory'?<Directory groups={groups} roles={roles} busy={busy} form={newGroup} setForm={setNewGroup} create={createGroup} refresh={()=>run(loadDirectory,'Directory refreshed.',null)}/>:
      <Security logs={logs} authentication={overview?.authentication} busy={busy} refresh={()=>run(loadSecurity,'Security log refreshed.',null)}/>
    }
  </>;
}

function Overview({data,busy,refresh}){
  const summary=data?.summary||{};
  const protocol=data?.protocol||{};
  const auth=data?.authentication||{};
  return <div className="identity-grid">
    <Card title="Live identity platform">
      <dl className="detail-list">
        <div><dt>Issuer</dt><dd>{identityHost(SOUSA_MURRAY_IDENTITY.issuer)}</dd></div>
        <div><dt>Protocol</dt><dd>OpenID Connect / OAuth 2.0</dd></div>
        <div><dt>PKCE</dt><dd>{protocol.pkceRequired?'Required':'Not reported'}</dd></div>
        <div><dt>Signing</dt><dd>{protocol.signingKey?.algorithm||'RS256'} · {protocol.signingKey?.kid||'—'}</dd></div>
        <div><dt>Account Centre</dt><dd>{data?.accountCentre||'https://my.sousamurray.co.uk'}</dd></div>
      </dl>
      <div className="identity-card-actions"><button className="button secondary govuk-button govuk-button--secondary" onClick={refresh} disabled={busy}><RefreshCw size={15}/>Refresh</button></div>
    </Card>

    <Card title="Directory">
      <dl className="detail-list">
        <div><dt>Identities</dt><dd>{summary.identities??'—'}</dd></div>
        <div><dt>Verified identities</dt><dd>{summary.verifiedIdentities??'—'}</dd></div>
        <div><dt>Active applications</dt><dd>{summary.activeApplications??'—'}</dd></div>
        <div><dt>Active sessions</dt><dd>{summary.activeSessions??'—'}</dd></div>
        <div><dt>Security events · 24h</dt><dd>{summary.events24h??'—'}</dd></div>
        <div><dt>Failures · 24h</dt><dd>{summary.failures24h??'—'}</dd></div>
      </dl>
    </Card>

    <Card title="Authentication methods">
      <div className="identity-route-grid">
        <div><span>Password</span><strong>{auth.password?.enabled?'Enabled':'Disabled'}</strong></div>
        <div><span>Passkeys</span><strong>{auth.passkeys?.enabled?'Enabled':'Disabled'}</strong></div>
        <div><span>Authenticator MFA</span><strong>{auth.totp?.enabled?'Enabled':'Disabled'}</strong></div>
        <div><span>MFA encryption</span><strong>{auth.totp?.encryptionConfigured?'Configured':'Not configured'}</strong></div>
      </div>
    </Card>
  </div>;
}

function Users({users,query,setQuery,search,busy,form,setForm,create,changeStatus,invite}){
  const columns=useMemo(()=>[
    {key:'display_name',label:'Name',render:r=>r.display_name||'—'},
    {key:'primary_email',label:'Email'},
    {key:'smid',label:'SMID',render:r=><code>{r.smid}</code>},
    {key:'email_verified_at',label:'Verified',render:r=>r.email_verified_at?'Yes':'No'},
    {key:'status',label:'Status',render:r=><Status value={r.status}/>},
    {key:'last_authenticated_at',label:'Last sign in',render:r=>r.last_authenticated_at||'Never'},
    {key:'actions',label:'Actions',render:r=><div className="identity-card-actions"><button className="button secondary small govuk-button govuk-button--secondary" onClick={()=>invite(r)} disabled={busy}>Send setup email</button><button className={`button small ${r.status==='active'?'danger-outline':'secondary'}`} onClick={()=>changeStatus(r,r.status==='active'?'suspended':'active')} disabled={busy}>{r.status==='active'?'Suspend':'Restore'}</button></div>},
  ],[busy,changeStatus,invite]);

  return <div className="identity-grid">
    <Card title="Find Sousa Murray IDs">
      <form className="identity-search" onSubmit={search}><Search size={17}/><input className="govuk-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search by name, email or SMID"/><button className="button primary govuk-button" disabled={busy}>Search</button></form>
      {users.length?<Table rows={users} columns={columns}/>:<div className="empty-inline">No matching Sousa Murray IDs.</div>}
    </Card>
    <Card title="Create managed identity">
      <form className="crm-form-grid" onSubmit={create}>
        <label className="crm-field wide govuk-label"><span>Full name</span><input className="govuk-input" required value={form.displayName} onChange={e=>setForm(v=>({...v,displayName:e.target.value}))}/></label>
        <label className="crm-field wide govuk-label"><span>Email address</span><input className="govuk-input" required type="email" value={form.email} onChange={e=>setForm(v=>({...v,email:e.target.value.toLowerCase()}))}/></label>
        <div className="crm-field wide"><button className="button primary govuk-button" disabled={busy}><Plus size={15}/>Create Sousa Murray ID</button></div>
      </form>
    </Card>
  </div>;
}

function Applications({applications,busy,form,setForm,create,refresh}){
  const columns=[
    {key:'name',label:'Application'},
    {key:'client_id',label:'Client ID',render:r=><code>{r.client_id}</code>},
    {key:'client_type',label:'Type',render:r=>label(r.client_type)},
    {key:'status',label:'Status',render:r=><Status value={r.status}/>},
    {key:'redirectUris',label:'Callback URLs',render:r=>(r.redirectUris||[]).join(', ')||'—'},
  ];
  return <div className="identity-grid">
    <Card title="Registered applications"><div className="identity-card-actions"><button className="button secondary govuk-button govuk-button--secondary" onClick={refresh} disabled={busy}><RefreshCw size={15}/>Refresh</button></div>{applications.length?<Table rows={applications} columns={columns}/>:<div className="empty-inline">No applications returned.</div>}</Card>
    <Card title="Register application">
      <form className="crm-form-grid" onSubmit={create}>
        <label className="crm-field wide govuk-label"><span>Application name</span><input className="govuk-input" required value={form.name} onChange={e=>setForm(v=>({...v,name:e.target.value}))}/></label>
        <label className="crm-field wide govuk-label"><span>Client ID</span><input className="govuk-input" required value={form.clientId} onChange={e=>setForm(v=>({...v,clientId:e.target.value}))} placeholder="my-application"/></label>
        <label className="crm-field govuk-label"><span>Client type</span><select className="govuk-select" value={form.clientType} onChange={e=>setForm(v=>({...v,clientType:e.target.value}))}><option value="first_party">First-party</option><option value="public">Public</option><option value="confidential">Confidential</option></select></label>
        <label className="crm-field wide govuk-label"><span>Callback URL</span><input className="govuk-input" required value={form.redirectUri} onChange={e=>setForm(v=>({...v,redirectUri:e.target.value}))}/></label>
        <div className="crm-field wide"><button className="button primary govuk-button" disabled={busy}><Plus size={15}/>Register application</button></div>
      </form>
    </Card>
  </div>;
}

function Directory({groups,roles,busy,form,setForm,create,refresh}){
  return <div className="identity-grid">
    <Card title="Groups">
      <div className="identity-card-actions"><button className="button secondary govuk-button govuk-button--secondary" onClick={refresh} disabled={busy}><RefreshCw size={15}/>Refresh</button></div>
      {groups.length?<Table rows={groups} columns={[{key:'display_name',label:'Group'},{key:'group_type',label:'Type',render:r=>label(r.group_type)},{key:'status',label:'Status',render:r=><Status value={r.status}/>},{key:'member_count',label:'Members'}]}/>:<div className="empty-inline">No directory groups returned.</div>}
    </Card>
    <Card title="Create group">
      <form className="crm-form-grid" onSubmit={create}>
        <label className="crm-field wide govuk-label"><span>Group name</span><input className="govuk-input" required value={form.displayName} onChange={e=>setForm(v=>({...v,displayName:e.target.value}))}/></label>
        <label className="crm-field wide govuk-label"><span>Description</span><textarea className="govuk-textarea" rows="3" value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))}/></label>
        <label className="crm-field govuk-label"><span>Type</span><select className="govuk-select" value={form.groupType} onChange={e=>setForm(v=>({...v,groupType:e.target.value}))}><option value="security">Security</option><option value="dynamic">Dynamic</option></select></label>
        <div className="crm-field wide"><button className="button primary govuk-button" disabled={busy}><Plus size={15}/>Create group</button></div>
      </form>
    </Card>
    <Card title="Administrative roles">
      {roles.length?<Table rows={roles} columns={[{key:'display_name',label:'Role'},{key:'role_key',label:'Role key',render:r=><code>{r.role_key}</code>},{key:'privileged',label:'Privileged',render:r=>r.privileged?'Yes':'No'},{key:'assignment_count',label:'Assignments'}]}/>:<div className="empty-inline">No directory roles returned.</div>}
    </Card>
  </div>;
}

function Security({logs,authentication,busy,refresh}){
  return <div className="identity-grid">
    <Card title="Authentication policy">
      <dl className="detail-list">
        <div><dt>Password minimum</dt><dd>{authentication?.password?.minimumLength??12} characters</dd></div>
        <div><dt>Verified email required</dt><dd>{authentication?.password?.requireVerifiedEmail?'Yes':'No'}</dd></div>
        <div><dt>Passkeys</dt><dd><Status value={authentication?.passkeys?.enabled?'enabled':'disabled'}/></dd></div>
        <div><dt>Authenticator MFA</dt><dd><Status value={authentication?.totp?.enabled?'enabled':'disabled'}/></dd></div>
      </dl>
    </Card>
    <Card title="Security events">
      <div className="identity-card-actions"><button className="button secondary govuk-button govuk-button--secondary" onClick={refresh} disabled={busy}><RefreshCw size={15}/>Refresh</button></div>
      {logs.length?<Table rows={logs} columns={[
        {key:'created_at',label:'Date'},
        {key:'event_type',label:'Event',render:r=><code>{r.event_type}</code>},
        {key:'smid',label:'SMID',render:r=>r.smid?<code>{r.smid}</code>:'—'},
        {key:'client_id',label:'Application'},
        {key:'outcome',label:'Outcome',render:r=><Status value={r.outcome}/>}
      ]}/>:<div className="empty-inline">No identity security events returned.</div>}
    </Card>
  </div>;
}
